import { useTranslation } from "react-i18next";
import { Box, Grid } from "@mui/material";
import { PageLayout } from "@propai-web/lib/components/shared/PageLayout";
import { createFileRoute } from "@tanstack/react-router";

import {
  GraphWithControls,
  HeatPowerChartContainer,
  IndoorTemperatureChartContainer,
  OptimizationChartContainer,
  useGraphControlState,
  useHeatPowerData,
  useIndoorTemperatureData,
  useOptimizationOffsetData,
} from "@utilifeed/components/propai";
import { useGetFlexStations } from "@core/apis/PropAI/hooks";

import { RadiatorCurveSection, SettingController } from "../../../../components/Optimization";

function PropertyDetailOptimization() {
  const { t } = useTranslation(["propai_properties"]);
  const substationId = Route.useParams().substationId;
  const routeContextAddress = Route.useRouteContext()?.substation?.install_address?.["street"] as
    | string
    | undefined;
  const { data: flexStations } = useGetFlexStations(substationId);
  const flexStationUid = flexStations?.[0]?.uid;

  // Graph control groups
  const radiatorGroup = useGraphControlState();
  const indoorTemperatureGroup = useGraphControlState();

  // Data hooks
  const { data: offsetData, isLoading: isOffsetChartLoading } = useOptimizationOffsetData(
    substationId,
    radiatorGroup.dateRange,
    { flexStationUid }
  );

  const { data: heatPowerData, isLoading: isHeatPowerLoading } = useHeatPowerData(
    substationId,
    radiatorGroup.dateRange
  );

  const { data: indoorTempData, isLoading: indoorTempLoading } = useIndoorTemperatureData(
    substationId,
    indoorTemperatureGroup.dateRange,
    { flexStationUid }
  );

  return (
    <PageLayout>
      <Grid item xs={12}>
        <SettingController
          substationId={substationId}
          flexStationUid={flexStationUid}
          routeContextAddress={routeContextAddress}
        />
      </Grid>

      <Grid item xs={12}>
        <GraphWithControls
          title={t("title_radiator_curve_and_offset")}
          showControls
          showQuickDates
          showDataSettings
          dateRange={radiatorGroup.dateRange}
          onDateRangeChange={radiatorGroup.setDateRange}
          resolution={radiatorGroup.resolution}
          onResolutionChange={radiatorGroup.setResolution}
        >
          <RadiatorCurveSection
            dateRange={radiatorGroup.dateRange}
            resolution={radiatorGroup.resolution}
            {...offsetData.radiatorCurveProps}
          />
          <OptimizationChartContainer
            data={offsetData.optimizationChartData}
            isLoading={isOffsetChartLoading}
            dateRange={radiatorGroup.dateRange}
            resolution={radiatorGroup.resolution}
            graphSettings={radiatorGroup.graphSettings}
          />
          <Box mt={4}>
            <HeatPowerChartContainer
              data={heatPowerData.heatPowerChartData}
              isLoading={isHeatPowerLoading}
              dateRange={radiatorGroup.dateRange}
              resolution={radiatorGroup.resolution}
              graphSettings={radiatorGroup.graphSettings}
            />
          </Box>
        </GraphWithControls>
      </Grid>

      <Grid item xs={12}>
        <GraphWithControls
          title={t("title_indoor_temperature_and_target")}
          showControls
          showQuickDates
          showDataSettings
          dateRange={indoorTemperatureGroup.dateRange}
          onDateRangeChange={indoorTemperatureGroup.setDateRange}
          resolution={indoorTemperatureGroup.resolution}
          onResolutionChange={indoorTemperatureGroup.setResolution}
        >
          <IndoorTemperatureChartContainer
            data={indoorTempData.indoorTemperatureChartData}
            isLoading={indoorTempLoading}
            dateRange={indoorTemperatureGroup.dateRange}
            resolution={indoorTemperatureGroup.resolution}
          />
        </GraphWithControls>
      </Grid>
    </PageLayout>
  );
}

export const Route = createFileRoute("/$organizationId/properties/$substationId/optimization")({
  component: PropertyDetailOptimization,
});
